import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as yup from "yup";
import { NINData } from "../../@types";
import useUpdateAccount from "../../hooks/useUpdateAccount";
import SignUpNavbar from "../../components/SignUpNavbar";
import useGetPhoneOTP from "../../hooks/useGetPhoneOTP";
import { HashLoader } from "react-spinners";
import { differenceInYears } from "date-fns";
import { getCountryCallingCode } from "react-phone-number-input";
import BasePhoneInput from "../../components/BasePhoneInput";
import { useAuth } from "../../context/AuthContext";
import useResolveGeo from "../../hooks/useResolveGeo";
import {
  defaultPhoneCodeFor,
  normalizePhoneCode,
  phoneInputCountry,
  splitPhoneNumber,
} from "../../utils/phoneNumber";
import type { User } from "../../@types";

const schema = yup.object().shape({
  firstname: yup.string().required("First name is required"),
  lastname: yup.string().required("Last name is required"),
  middlename: yup.string(),
  gender: yup
    .string()
    .oneOf(["male", "female"], "Please select a valid gender")
    .required("Gender is required"),
  date_of_birth: yup
    .date()
    .required("Date of Birth is required")
    .test("age", "You must be at least 18 years old", (value) => {
      return value && differenceInYears(new Date(), new Date(value)) >= 18;
    }),
  place_of_birth: yup.string().required("Place of Birth is required"),
  phone: yup.string().required("Phone number is required"),
});

const CompleteProfile = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const navigate = useNavigate();
  const { user } = useAuth();
  // Opens the country picker where the customer actually is. Detection failing
  // is fine — `country` is null and phoneInputCountry falls back to NG.
  const { country } = useResolveGeo();
  const defaultPhoneCode = defaultPhoneCodeFor(user?.phone_code, country);

  const { state } = useLocation();

  useEffect(() => {
    if (state?.nin_data) {
      sessionStorage.setItem("nin_data", JSON.stringify(state.nin_data));
    }
  }, [state?.nin_data]);

  const ninData: NINData = useMemo(() => {
    if (state?.nin_data) {
      return state.nin_data as NINData;
    }

    const stored = sessionStorage.getItem("nin_data");
    return stored ? (JSON.parse(stored) as NINData) : ({} as NINData);
  }, [state?.nin_data]);

  const getNextProfilePath = useCallback((profile?: User | null) => {
    // Password is the final data step. Username and profile photo are
    // optional dashboard setup tasks, not signup screens.
    if (!profile?.password_set) return "/sign-up/create-password";
    return "/home";
  }, []);

  const profileAlreadyCompleted = useMemo(() => {
    return Boolean(
      user?.firstname &&
        user?.lastname &&
        (user?.phone || user?.phone_number) &&
        user?.date_of_birth
    );
  }, [
    user?.firstname,
    user?.lastname,
    user?.phone,
    user?.phone_number,
    user?.date_of_birth,
  ]);

  useEffect(() => {
    if (profileAlreadyCompleted) {
      navigate(getNextProfilePath(user), { replace: true });
    }
  }, [getNextProfilePath, profileAlreadyCompleted, navigate, user]);

  const {
    updateAccount,
    updatingAccount,
    updateAccountError,
    isUpdatingAccountError,
  } = useUpdateAccount();

  const { getOTP, gettingPhoneOTP } = useGetPhoneOTP();

  const isLoading = useMemo(
    () => updatingAccount || gettingPhoneOTP,
    [updatingAccount, gettingPhoneOTP]
  );

  return (
    <div>
      {isLoading && (
        <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      <Formik
        initialValues={{
          firstname: ninData?.first_name || ninData?.firstName || user?.firstname || "",
          lastname: ninData?.last_name || ninData?.lastName || user?.lastname || "",
          middlename: ninData?.middle_name || ninData?.middleName || "nil",
          gender: ninData?.gender?.toLowerCase() || "",
          place_of_birth: "",
          phone_code: defaultPhoneCode,
          phone: ninData?.phone_number || ninData?.mobile || user?.phone || user?.phone_number || "",
          dateOfBirth: ninData?.date_of_birth || ninData?.dateOfBirth || user?.date_of_birth || null,
          date_of_birth: ninData?.date_of_birth || ninData?.dateOfBirth || user?.date_of_birth || null,
        }}
        validationSchema={schema}
        onSubmit={(values) => {
          if (values.date_of_birth) {
            const formattedDateOfBirth = new Date(values.date_of_birth)
              .toISOString()
              .split("T")[0]; // Formats date as YYYY-MM-DD

            updateAccount(
              {
                firstname: values.firstname,
                lastname: values.lastname,
                ...splitPhoneNumber(values.phone, values.phone_code),
                nin: ninData?.idNumber,
                middlename: values.middlename,
                gender: values.gender,
                place_of_birth: values.place_of_birth,
                date_of_birth: formattedDateOfBirth,
              },
              {
                onError(error) {
                  console.log(error);
                  if (
                    profileAlreadyCompleted &&
                    error.message?.toLowerCase().includes("phone") &&
                    error.message?.toLowerCase().includes("taken")
                  ) {
                    navigate(getNextProfilePath(user), { replace: true });
                  }
                },
                onSuccess(data) {
                  const updatedUser = data.data;
                  if (updatedUser?.phone && !updatedUser?.phone_verified) {
                    getOTP().then(() => {
                      navigate(
                        "/sign-up/verify-phone-number?redirect=/sign-up/create-password",
                        {
                          state: {
                            phone: splitPhoneNumber(
                              values.phone,
                              values.phone_code
                            ).phone,
                            redirect: "/sign-up/create-password",
                          },
                        }
                      );
                    });
                  } else {
                    navigate(getNextProfilePath(updatedUser), { replace: true });
                  }
                },
              }
            );
          }
        }}
      >
        {({ isValid, values, handleChange, handleBlur, setFieldValue }) => {
          return (
            <div>
              <SignUpNavbar />
              <div className="flex flex-col justify-center items-center p-20 px-5 md:px-20 gap-8 text-lg">
                <div className="flex flex-col justify-center items-center gap-2">
                  <div className="text-4xl md:text-3xl lg:text-5xl max-w-3xl font-bold text-center">
                    Complete your Profile
                  </div>
                  <div className="text-gray-500 text-center max-w-lg">
                    Confirm your personal details to continue.
                  </div>
                </div>
                <Form className="w-full flex-col gap-5 flex items-center justify-center text-base">
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    <label
                      htmlFor="firstname"
                      className="text-sm font-bold text-gray-700"
                    >
                      First Name*
                    </label>
                    <Field
                      type="text"
                      placeholder="Enter your first name"
                      name="firstname"
                      id="firstname"
                      disabled={!!ninData?.firstName}
                      className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                    />
                    <ErrorMessage
                      name="firstname"
                      component="div"
                      className="text-red-500"
                    />
                  </div>
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    <label
                      htmlFor="middlename"
                      className="text-sm font-bold text-gray-700"
                    >
                      Middle Name
                    </label>
                    <Field
                      type="text"
                      placeholder="Enter your middle name"
                      name="middlename"
                      id="middlename"
                      disabled={!!ninData?.middleName}
                      className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                    />
                    <ErrorMessage
                      name="middlename"
                      component="div"
                      className="text-red-500"
                    />
                  </div>
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    <label
                      htmlFor="lastname"
                      className="text-sm font-bold text-gray-700"
                    >
                      Last Name*
                    </label>
                    <Field
                      type="text"
                      placeholder="Enter your last name"
                      name="lastname"
                      id="lastname"
                      disabled={!!ninData?.lastName}
                      className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                    />
                    <ErrorMessage
                      name="lastname"
                      component="div"
                      className="text-red-500"
                    />
                  </div>

                  <div className="flex flex-col gap-2 max-w-md w-full">
                    <label
                      htmlFor="phone"
                      className="text-sm font-bold text-gray-700"
                    >
                      Phone Number*
                    </label>

                    <BasePhoneInput
                      placeholder="Enter your phone number"
                      international
                      countryCallingCodeEditable={false}
                      defaultCountry={phoneInputCountry(country)}
                      value={values.phone}
                      onChange={(value: string | undefined) => {
                        setFieldValue("phone", value);
                      }}
                      onCountryChange={(country: any) => {
                        if (country) {
                          const code = getCountryCallingCode(country);
                          setFieldValue("phone_code", normalizePhoneCode(code));
                        }
                      }}
                      onBlur={handleBlur("phone")}
                      name="phone"
                      id="phone"
                      className="py-1 focus:outline-none flex-1 flex items-center rounded-lg border border-gray-300 shadow px-4 w-full focus-within:outline-deepBlue focus-within:outline"
                    />
                    <ErrorMessage
                      name="phone"
                      component="div"
                      className="text-red-500"
                    />
                  </div>
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    <label
                      htmlFor="gender"
                      className="text-sm font-bold text-gray-700"
                    >
                      Gender*
                    </label>
                    <Field
                      as="select"
                      name="gender"
                      id="gender"
                      className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full bg-white"
                    >
                      <option value="" disabled>
                        Select your gender
                      </option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </Field>
                    <ErrorMessage
                      name="gender"
                      component="div"
                      className="text-red-500"
                    />
                  </div>
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    <label
                      htmlFor="date_of_birth"
                      className="text-sm font-bold text-gray-700"
                    >
                      Date of Birth*
                    </label>
                    <input
                      onBlur={handleBlur("date_of_birth")}
                      onChange={handleChange("date_of_birth")}
                      type="date"
                      value={values.date_of_birth || ""}
                      placeholder="Enter your date of birth"
                      name="date_of_birth"
                      id="date_of_birth"
                      className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                    />
                    <ErrorMessage
                      name="date_of_birth"
                      component="div"
                      className="text-red-500"
                    />
                  </div>
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    <label
                      htmlFor="place_of_birth"
                      className="text-sm font-bold text-gray-700"
                    >
                      Place of Birth*
                    </label>
                    <Field
                      type="text"
                      placeholder="Enter your place of birth"
                      name="place_of_birth"
                      id="place_of_birth"
                      className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                    />
                    <ErrorMessage
                      name="place_of_birth"
                      component="div"
                      className="text-red-500"
                    />
                  </div>
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    {isUpdatingAccountError && (
                      <div className="text-red-500 text-left flex">
                        {updateAccountError?.message}
                      </div>
                    )}
                    <button
                      disabled={!isValid}
                      type="submit"
                      className="p-4 px-5 w-full border-visaro-gray-700 border rounded-lg font-bold bg-deepBlue text-white text-sm"
                    >
                      {updatingAccount ? "Loading" : "Continue"}
                    </button>
                  </div>
                </Form>
              </div>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};

export default CompleteProfile;
